import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { ExternalLink, ArrowLeft, Check, Clock, Lock, Hammer, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const statusClass = { Live: "status-live", MVP: "status-mvp", Idea: "status-idea" };
const remunerationOptions = ["Equity", "Token allocation", "Salary", "Revenue share", "Open to discuss"];
const investmentSizes = ["< $50K", "$50K-$250K", "$250K-$1M", "$1M+", "Flexible"];

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { user, api } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBuilderForm, setShowBuilderForm] = useState(false);
  const [showInvestorForm, setShowInvestorForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [builderSkills, setBuilderSkills] = useState("");
  const [builderRole, setBuilderRole] = useState("");
  const [builderIdeas, setBuilderIdeas] = useState("");
  const [builderRemuneration, setBuilderRemuneration] = useState([]);

  const [investorWho, setInvestorWho] = useState("");
  const [investorThesis, setInvestorThesis] = useState("");
  const [investorSize, setInvestorSize] = useState("");
  const [investorInterest, setInvestorInterest] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${projectId}`)
      .then((r) => setProject(r.data))
      .catch(() => toast.error("Project not found"))
      .finally(() => setLoading(false));
  }, [projectId, api]);

  const submitRequest = async (type) => {
    setSubmitting(true);
    try {
      const form_data = type === "builder"
        ? { skills: builderSkills, role: builderRole, ideas: builderIdeas, remuneration: builderRemuneration }
        : { who: investorWho, thesis: investorThesis, size: investorSize, interest: investorInterest };
      await api.post("/requests", { project_id: projectId, type, form_data });
      toast.success("Request submitted successfully");
      setShowBuilderForm(false);
      setShowInvestorForm(false);
      const r = await api.get(`/projects/${projectId}`);
      setProject(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="spinner"></div></div>;
  if (!project) return <div className="min-h-screen flex items-center justify-center pt-20"><p className="text-slate-400">Project not found</p></div>;

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const isPublic = project.is_public_view;
  const hasRole = project.user_role;
  const hasPending = project.has_pending_request;

  return (
    <main className="min-h-screen bg-white pt-24 md:pt-28 pb-24" data-testid="project-detail-page">
      {/* Background image hero */}
      {project.background_image && (
        <div className="relative -mt-28 mb-8">
          <div className="h-64 md:h-80 overflow-hidden">
            <img src={`${BACKEND}${project.background_image}`} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent"></div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Link to="/portfolio" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 mb-8 font-sans transition-colors" data-testid="back-to-portfolio">
          <ArrowLeft className="w-4 h-4" /> Back to Portfolio
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-start gap-4 mb-2">
            {project.logo ? (
              <img src={`${BACKEND}${project.logo}`} alt="" className="w-14 h-14 rounded-xl object-cover border border-slate-200" />
            ) : (
              <div className="w-3 h-12 rounded-full" style={{ backgroundColor: project.brand_color }}></div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">{project.category}</span>
                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${statusClass[project.status]}`}>{project.status}</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">{project.name}</h1>
              <p className="font-sans text-base md:text-lg text-slate-500 mt-2">{project.tagline}</p>
              {project.url && (
                <a href={project.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-600 hover:text-blue-700 font-mono" data-testid="project-external-link">
                  <ExternalLink className="w-3.5 h-3.5" /> {project.url.replace("https://", "")}
                </a>
              )}
            </div>
          </div>
        </motion.div>

        <div className="section-gradient-line my-8"></div>

        {project.description && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-10">
            <p className="font-sans text-base text-slate-600 leading-relaxed whitespace-pre-line">{project.description}</p>
          </motion.div>
        )}

        {isPublic && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center my-10" data-testid="public-cta-box">
            <Lock className="w-6 h-6 text-slate-400 mx-auto mb-3" />
            <h3 className="font-serif text-xl font-bold text-slate-900 mb-2">Want to see more?</h3>
            <p className="font-sans text-sm text-slate-500 mb-4">Sign in to see full details, join as a Builder, or explore investment opportunities.</p>
            <Link to="/auth">
              <Button className="bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans" data-testid="project-signin-cta">Sign In to Access</Button>
            </Link>
          </div>
        )}

        {!isPublic && (
          <>
            {project.features?.length > 0 && (
              <div className="mb-10">
                <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400 mb-4">Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {project.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm font-sans text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {project.api_docs && (
              <div className="mb-10">
                <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400 mb-4">API Documentation</h3>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6"><pre className="font-mono text-sm text-slate-600 whitespace-pre-wrap">{project.api_docs}</pre></div>
              </div>
            )}
            {project.market_analysis && (
              <div className="mb-10">
                <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400 mb-4">Market Analysis</h3>
                <p className="font-sans text-sm text-slate-600 leading-relaxed whitespace-pre-line">{project.market_analysis}</p>
              </div>
            )}
            {project.team?.length > 0 && (
              <div className="mb-10">
                <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400 mb-4">Team</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {project.team.map((t, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full flex items-center justify-center text-white text-xs font-bold">{t.name?.[0]?.toUpperCase()}</div>
                      <div><p className="font-sans text-sm font-medium text-slate-900">{t.name}</p><p className="font-mono text-xs text-slate-400">{t.role}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {project.screenshots?.length > 0 && (
              <div className="mb-10">
                <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400 mb-4">Screenshots</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {project.screenshots.map((s, i) => (
                    <img key={i} src={`${BACKEND}${s}`} alt={`Screenshot ${i + 1}`} className="rounded-xl border border-slate-200 w-full object-cover" data-testid={`screenshot-${i}`} />
                  ))}
                </div>
              </div>
            )}
            {project.extended_docs && (hasRole || project.is_admin) && (
              <div className="mb-10">
                <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400 mb-4">Extended Documentation</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6"><p className="font-sans text-sm text-slate-700 whitespace-pre-line">{project.extended_docs}</p></div>
              </div>
            )}

            <div className="section-gradient-line my-8"></div>
            <div className="flex flex-wrap gap-4">
              {hasRole ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-6 py-3 inline-flex items-center gap-2" data-testid="role-badge">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span className="font-sans text-sm text-emerald-700 font-medium capitalize">You are a {project.user_role} on this project</span>
                </div>
              ) : hasPending ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-6 py-3 inline-flex items-center gap-2" data-testid="pending-badge">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="font-sans text-sm text-amber-700 font-medium capitalize">Your {project.pending_request_type} request is pending</span>
                </div>
              ) : (
                <>
                  <Button onClick={() => setShowBuilderForm(true)} className="bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans gap-2" data-testid="join-builder-btn"><Hammer className="w-4 h-4" /> Join as Builder</Button>
                  <Button onClick={() => setShowInvestorForm(true)} variant="outline" className="border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-600 font-sans gap-2" data-testid="join-investor-btn"><TrendingUp className="w-4 h-4" /> Join as Investor</Button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={showBuilderForm} onOpenChange={setShowBuilderForm}>
        <DialogContent className="max-w-lg" data-testid="builder-form-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Join as Builder</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="font-sans text-sm">What can you bring to this project?</Label><Textarea value={builderSkills} onChange={(e) => setBuilderSkills(e.target.value)} placeholder="Describe your skills and experience..." className="mt-1" data-testid="builder-skills-input" /></div>
            <div><Label className="font-sans text-sm">Proposed role / skills</Label><Input value={builderRole} onChange={(e) => setBuilderRole(e.target.value)} placeholder="e.g. Frontend Developer, Smart Contract Engineer..." className="mt-1" data-testid="builder-role-input" /></div>
            <div><Label className="font-sans text-sm">Ideas for the project</Label><Textarea value={builderIdeas} onChange={(e) => setBuilderIdeas(e.target.value)} placeholder="Share your ideas..." className="mt-1" data-testid="builder-ideas-input" /></div>
            <div>
              <Label className="font-sans text-sm mb-2 block">Preferred form of remuneration</Label>
              <div className="space-y-2">
                {remunerationOptions.map((opt) => (
                  <div key={opt} className="flex items-center gap-2">
                    <Checkbox checked={builderRemuneration.includes(opt)} onCheckedChange={(checked) => setBuilderRemuneration(checked ? [...builderRemuneration, opt] : builderRemuneration.filter(r => r !== opt))} data-testid={`remuneration-${opt.toLowerCase().replace(/\s/g, '-')}`} />
                    <span className="font-sans text-sm text-slate-600">{opt}</span>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={() => submitRequest("builder")} disabled={submitting || !builderSkills} className="w-full bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans" data-testid="submit-builder-request-btn">{submitting ? "Submitting..." : "Submit Request"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvestorForm} onOpenChange={setShowInvestorForm}>
        <DialogContent className="max-w-lg" data-testid="investor-form-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Join as Investor</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="font-sans text-sm">Who are you / your organization?</Label><Textarea value={investorWho} onChange={(e) => setInvestorWho(e.target.value)} placeholder="Tell us about yourself..." className="mt-1" data-testid="investor-who-input" /></div>
            <div><Label className="font-sans text-sm">Investment thesis and objectives</Label><Textarea value={investorThesis} onChange={(e) => setInvestorThesis(e.target.value)} placeholder="What's your investment approach..." className="mt-1" data-testid="investor-thesis-input" /></div>
            <div>
              <Label className="font-sans text-sm">Typical investment size range</Label>
              <Select value={investorSize} onValueChange={setInvestorSize}>
                <SelectTrigger className="mt-1" data-testid="investor-size-select"><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>{investmentSizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="font-sans text-sm">What interests you about this project?</Label><Textarea value={investorInterest} onChange={(e) => setInvestorInterest(e.target.value)} placeholder="What caught your attention..." className="mt-1" data-testid="investor-interest-input" /></div>
            <Button onClick={() => submitRequest("investor")} disabled={submitting || !investorWho} className="w-full bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans" data-testid="submit-investor-request-btn">{submitting ? "Submitting..." : "Submit Request"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
